<div class="p-4 shift-summary-output">
  <div class="flex align-items-center mb-4">
    <p-button icon="pi pi-arrow-left" class="p-button-text mr-2" (onClick)="cancel()"></p-button>
    <h2 class="m-0">{{ title() }}</h2>
    @if (currentSummary()?.isSubmitted) {
      <p-tag value="Submitted" severity="success" class="ml-3"></p-tag>
    } @else if (currentSummary()) {
      <p-tag value="Draft" severity="warn" class="ml-3"></p-tag>
    }
  </div>

  @if (loading()) {
    <p-progressSpinner></p-progressSpinner>
  }

  @if (validationError()) {
    <p-message
      severity="error"
      [text]="validationError()!"
      class="mb-3 w-full">
    </p-message>
  }

  @if (currentSummary()?.isSubmitted) {
    <p-panel header="Submission Details" class="mb-3">
      <div class="grid">
        <div class="col-12 md:col-6">
          <span class="field-label">Submitted At</span>
          <span class="field-value">{{ currentSummary()?.submittedAtUtc | date:'medium' }}</span>
        </div>
        <div class="col-12 md:col-6">
          <span class="field-label">Submitted By</span>
          <span class="field-value">{{ formatNA(currentSummary()?.submittedBy) }}</span>
        </div>
      </div>
    </p-panel>
  }

  <div class="shift-summary-top-grid">
    <div class="field">
      <div class="flex align-items-center justify-content-between mb-2">
        <label for="shiftStart" class="mb-0">Shift Start *</label>
        <p-button
          icon="pi pi-clock"
          class="p-button-rounded p-button-text p-button-sm"
          [disabled]="isReadOnly()"
          pTooltip="Select a nearby 12-hour shift"
          (onClick)="openShiftShortcutPopover($event, shiftShortcutPopover)">
        </p-button>
      </div>
      <p-datePicker
        id="shiftStart"
        [(ngModel)]="shiftStartUtc"
        (ngModelChange)="onShiftStartChanged($event)"
        [showTime]="true"
        [hourFormat]="'24'"
        [disabled]="isReadOnly()"
        class="w-full">
      </p-datePicker>

      <p-popover #shiftShortcutPopover appendTo="body">
        <div class="shift-shortcuts-popover">
          <div class="text-sm text-color-secondary mb-2">12-hour shift shortcuts</div>
          @if (shiftShortcutOptions().length) {
            <div class="shift-shortcuts-list">
              @for (shortcut of shiftShortcutOptions(); track shortcut.label) {
                <p-button
                  [label]="shortcut.label"
                  class="p-button-text p-button-sm shift-shortcut-option"
                  (onClick)="applyShiftShortcut(shortcut, shiftShortcutPopover)">
                </p-button>
              }
            </div>
          } @else {
            <span class="text-sm text-color-secondary">No shift shortcuts available.</span>
          }
        </div>
      </p-popover>
    </div>

    <div class="field">
      <div class="flex align-items-center justify-content-between mb-2">
        <label for="shiftEnd" class="mb-0">Shift End *</label>
        <p-button
          icon="pi pi-clock"
          class="p-button-rounded p-button-text p-button-sm"
          [disabled]="!shiftStartUtc || isReadOnly()"
          pTooltip="Set Shift End to Start + 12h"
          (onClick)="setShiftEndToTwelveHoursPastStart()">
        </p-button>
      </div>
      <p-datePicker
        id="shiftEnd"
        [(ngModel)]="shiftEndUtc"
        [showTime]="true"
        [hourFormat]="'24'"
        [disabled]="isReadOnly()"
        class="w-full">
      </p-datePicker>
    </div>

    <div class="field">
      <label for="dayNight">Day or Night Shift</label>
      <p-select
        id="dayNight"
        [(ngModel)]="dayNight"
        [options]="dayNightOptions"
        [disabled]="isReadOnly()"
        class="w-full"
        (ngModelChange)="onDayNightChanged($event)">
      </p-select>
    </div>

    <div class="field">
      <label for="crewMembers">Crew Members *</label>
      <p-multiSelect
        id="crewMembers"
        [(ngModel)]="selectedCrewMemberUserIds"
        [options]="crewMemberOptions()"
        [disabled]="isReadOnly()"
        placeholder="Select crew members"
        class="w-full">
      </p-multiSelect>
    </div>
  </div>

  <div class="grid shift-summary-grid">
    <div class="col-12 field">
      <label for="safetyTopicsDiscussed">Safety Topic(s) Discussed</label>
      <textarea
        pTextarea
        id="safetyTopicsDiscussed"
        [(ngModel)]="safetyTopicsDiscussed"
        rows="3"
        [disabled]="isReadOnly()"
        class="w-full"
        placeholder="Pinch points, job hazards, and other safety topics reviewed for this shift">
      </textarea>
    </div>

    <div class="col-12 md:col-4 field">
      <div class="question-field">
        <label class="question-field-label">Any Safety Incidents?</label>
        <div class="question-field-options">
          <div class="question-field-option">
            <p-radiobutton
              inputId="anySafetyIncidentsYes"
              name="anySafetyIncidents"
              [value]="true"
              [(ngModel)]="anySafetyIncidents"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="anySafetyIncidentsYes" class="mb-0">Yes</label>
          </div>
          <div class="question-field-option">
            <p-radiobutton
              inputId="anySafetyIncidentsNo"
              name="anySafetyIncidents"
              [value]="false"
              [(ngModel)]="anySafetyIncidents"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="anySafetyIncidentsNo" class="mb-0">No</label>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 md:col-4 field">
      <label for="currentStatus">Current Status *</label>
      <p-select
        id="currentStatus"
        [(ngModel)]="currentStatus"
        [options]="statusOptions"
        [disabled]="isReadOnly()"
        placeholder="Select status"
        class="w-full">
      </p-select>
    </div>

    @if (anySafetyIncidents === true) {
      <div class="col-12 field">
        <label for="safetyIncidentDetails">Safety Incident Details *</label>
        <textarea
          pTextarea
          id="safetyIncidentDetails"
          [(ngModel)]="safetyIncidentDetails"
          rows="3"
          [disabled]="isReadOnly()"
          class="w-full"
          placeholder="Provide context for the safety incident(s)">
        </textarea>
      </div>

      <div class="col-12 md:col-8 field">
        <label for="safetyIncidents">Incidents *</label>
        <p-multiSelect
          id="safetyIncidents"
          [(ngModel)]="safetyIncidents"
          [options]="incidentOptions"
          [disabled]="isReadOnly()"
          placeholder="Select incidents"
          class="w-full">
        </p-multiSelect>
      </div>
    }

    <div class="col-12 field">
      <label for="jobStatus">Job Status *</label>
      <textarea
        pTextarea
        id="jobStatus"
        [(ngModel)]="jobStatus"
        rows="4"
        [disabled]="isReadOnly()"
        class="w-full"
        placeholder="Overall job status during this shift">
      </textarea>
    </div>

    <div class="col-12 field">
      <label for="customerFeedback">Last Discussion with Customer *</label>
      <textarea
        pTextarea
        id="customerFeedback"
        [(ngModel)]="customerFeedback"
        rows="4"
        [disabled]="isReadOnly()"
        class="w-full"
        placeholder="Most recent discussion with the customer">
      </textarea>
    </div>

    <div class="col-12 md:col-6 field">
      <div class="question-field">
        <label class="question-field-label">Are all data components working properly?</label>
        <div class="question-field-options">
          <div class="question-field-option">
            <p-radiobutton
              inputId="dataComponentsWorkingYes"
              name="dataComponentsWorking"
              [value]="true"
              [(ngModel)]="dataComponentsWorking"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="dataComponentsWorkingYes" class="mb-0">Yes</label>
          </div>
          <div class="question-field-option">
            <p-radiobutton
              inputId="dataComponentsWorkingNo"
              name="dataComponentsWorking"
              [value]="false"
              [(ngModel)]="dataComponentsWorking"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="dataComponentsWorkingNo" class="mb-0">No</label>
          </div>
        </div>
      </div>
    </div>

    @if (dataComponentsWorking === false) {
      <div class="col-12 md:col-6 field">
        <label for="dataComponentsContext">Data Components Context *</label>
        <textarea
          pTextarea
          id="dataComponentsContext"
          [(ngModel)]="dataComponentsContext"
          rows="3"
          [disabled]="isReadOnly()"
          class="w-full"
          placeholder="Provide context for any data component issues">
        </textarea>
      </div>
    }

    <div class="col-12 md:col-6 field">
      <label for="fluidType">{{ fluidTypeLabel() }}</label>
      <p-select
        id="fluidType"
        [(ngModel)]="fluidType"
        [options]="fluidTypeOptions()"
        [disabled]="isReadOnly()"
        class="w-full">
      </p-select>
    </div>

    @if (supportsCurrentFluidSystem()) {
      <div class="col-12 md:col-6 field">
        <label for="currentFluidSystem">Current Fluid System</label>
        <p-select
          id="currentFluidSystem"
          [(ngModel)]="currentFluidSystem"
          [options]="currentFluidSystemOptions"
          [disabled]="isReadOnly()"
          placeholder="Select current fluid system"
          class="w-full">
        </p-select>
      </div>
    }

    @if (supportsFoamMetrics()) {
      <div class="col-12 md:col-3 field">
        <label for="foamStability">Foam Stability Time (min){{ requiresFoamAndSandMetrics() ? ' *' : '' }}</label>
        <p-inputNumber
          id="foamStability"
          [(ngModel)]="foamStabilityMinutes"
          [disabled]="isReadOnly()"
          [min]="0"
          mode="decimal"
          [minFractionDigits]="0"
          [maxFractionDigits]="2"
          class="w-full">
        </p-inputNumber>
      </div>

      <div class="col-12 md:col-3 field">
        <label for="sandSuspension">Sand Suspension Time (min){{ requiresFoamAndSandMetrics() ? ' *' : '' }}</label>
        <p-inputNumber
          id="sandSuspension"
          [(ngModel)]="sandSuspensionMinutes"
          [disabled]="isReadOnly()"
          [min]="0"
          mode="decimal"
          [minFractionDigits]="0"
          [maxFractionDigits]="2"
          class="w-full">
        </p-inputNumber>
      </div>
    }

    <div class="col-12 shift-summary-section-heading">
      <h3>LCM Report</h3>
    </div>

    <div class="col-12 md:col-6 field">
      <div class="question-field">
        <label class="question-field-label">Part C Test Status</label>
        <div class="question-field-options">
          <div class="question-field-option">
            <p-radiobutton
              inputId="partCTestStatusYes"
              name="partCTestStatus"
              [value]="partCTestStatusValues.yes"
              [(ngModel)]="partCTestStatus"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="partCTestStatusYes" class="mb-0">Yes</label>
          </div>
          <div class="question-field-option">
            <p-radiobutton
              inputId="partCTestStatusNo"
              name="partCTestStatus"
              [value]="partCTestStatusValues.no"
              [(ngModel)]="partCTestStatus"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="partCTestStatusNo" class="mb-0">No</label>
          </div>
          <div class="question-field-option">
            <p-radiobutton
              inputId="partCTestStatusNoLcm"
              name="partCTestStatus"
              [value]="partCTestStatusValues.noLcmUsePlanned"
              [(ngModel)]="partCTestStatus"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="partCTestStatusNoLcm" class="mb-0">No LCM Use Planned</label>
          </div>
        </div>
      </div>
    </div>

    @if (showPartCDetails()) {
      <div class="col-12 md:col-6 field">
        <label for="gramsToPass">Grams to Pass *</label>
        <p-inputNumber
          id="gramsToPass"
          [(ngModel)]="gramsToPass"
          [disabled]="isReadOnly()"
          [min]="0"
          class="w-full">
        </p-inputNumber>
      </div>

      <div class="col-12 md:col-6 field">
        <label for="waterLeakOff">Water Leak Off *</label>
        <p-inputNumber
          id="waterLeakOff"
          [(ngModel)]="waterLeakOff"
          [disabled]="isReadOnly()"
          [min]="0"
          class="w-full">
        </p-inputNumber>
      </div>
    }

    <div class="col-12 field">
      <label for="mechanicalNeeds">Mechanical Needs</label>
      <textarea
        pTextarea
        id="mechanicalNeeds"
        [(ngModel)]="mechanicalNeeds"
        rows="3"
        [disabled]="isReadOnly()"
        class="w-full"
        placeholder="Equipment or mechanical needs">
      </textarea>
    </div>

    <div class="col-12 md:col-3 field">
      <label for="iscav">ISCAV Gal Consumed</label>
      <p-inputNumber
        id="iscav"
        [(ngModel)]="iscavGallonsConsumed"
        [disabled]="isReadOnly()"
        [min]="0"
        class="w-full">
      </p-inputNumber>
    </div>

    <div class="col-12 md:col-3 field">
      <label for="procide">Procide Gal Consumed</label>
      <p-inputNumber
        id="procide"
        [(ngModel)]="procideGallonsConsumed"
        [disabled]="isReadOnly()"
        [min]="0"
        class="w-full">
      </p-inputNumber>
    </div>

    <div class="col-12 md:col-6 field">
      <div class="question-field">
        <label class="question-field-label">Is Fuel Provided by the Customer?</label>
        <div class="question-field-options">
          <div class="question-field-option">
            <p-radiobutton
              inputId="fuelProvidedByCustomerYes"
              name="fuelProvidedByCustomer"
              [value]="true"
              [(ngModel)]="fuelProvidedByCustomer"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="fuelProvidedByCustomerYes" class="mb-0">Yes</label>
          </div>
          <div class="question-field-option">
            <p-radiobutton
              inputId="fuelProvidedByCustomerNo"
              name="fuelProvidedByCustomer"
              [value]="false"
              [(ngModel)]="fuelProvidedByCustomer"
              [disabled]="isReadOnly()">
            </p-radiobutton>
            <label for="fuelProvidedByCustomerNo" class="mb-0">No</label>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 field">
      <label for="nextPlannedWork">Next Planned Work</label>
      <textarea
        pTextarea
        id="nextPlannedWork"
        [(ngModel)]="nextPlannedWork"
        rows="3"
        [disabled]="isReadOnly()"
        class="w-full"
        placeholder="Work planned for the next shift">
      </textarea>
    </div>
  </div>

  <p-panel header="Attachments" class="mb-3 mt-3">
    @if (!isReadOnly()) {
      <div class="attachment-upload mb-3">
        <label for="fileUpload">File Upload</label>
        <p-fileUpload
          id="fileUpload"
          [multiple]="true"
          [auto]="false"
          [customUpload]="true"
          [disabled]="isReadOnly()"
          (onSelect)="onFilesSelected($event)"
          (onRemove)="onFileRemoved($event)"
          [showUploadButton]="false"
          [showCancelButton]="false"
          chooseLabel="File Upload"
          [maxFileSize]="52428800">
          <ng-template #content let-files let-removeFileCallback="removeFileCallback" let-messages="messages">
            @if (files?.length) {
              <div class="attachment-list">
                @for (message of messages ?? []; track $index) {
                  <p-message
                    severity="error"
                    [text]="message.text"
                    class="mb-2">
                  </p-message>
                }

                @for (file of files ?? []; track file.name + '-' + file.lastModified + '-' + file.size; let index = $index) {
                  <div class="attachment-card">
                    <div class="attachment-card-preview">
                      @if (isPendingAttachmentImage(file) && getPendingAttachmentPreviewUrl(file)) {
                        <img
                          [src]="getPendingAttachmentPreviewUrl(file)!"
                          [alt]="file.name"
                          class="attachment-card-image">
                      } @else {
                        <div class="attachment-card-icon">
                          <i class="pi pi-file"></i>
                        </div>
                      }
                    </div>

                    <div class="attachment-card-body">
                      <div class="attachment-card-title">{{ file.name }}</div>
                      <div class="attachment-card-meta">
                        {{ (file.size / 1024) | number:'1.0-0' }} KB
                      </div>

                      <label [for]="'pending-description-' + index" class="attachment-card-label">Document Description</label>
                      <textarea
                        [id]="'pending-description-' + index"
                        pTextarea
                        rows="2"
                        [ngModel]="getPendingAttachmentDescription(file)"
                        (ngModelChange)="updatePendingAttachmentDescriptionForFile(file, $event)"
                        [disabled]="isReadOnly()"
                        class="w-full"
                        placeholder="Optional description for this file">
                      </textarea>
                    </div>

                    <div class="attachment-card-actions">
                      <p-button
                        icon="pi pi-trash"
                        severity="danger"
                        class="p-button-text p-button-sm"
                        [disabled]="isReadOnly()"
                        (onClick)="removePendingAttachment($event, index, file, removeFileCallback)">
                      </p-button>
                    </div>
                  </div>
                }
              </div>
            }
          </ng-template>
          <ng-template #empty>
            <div class="file-upload-hint">
              <p class="m-0 text-color-secondary">Drag and drop files here or click to select.</p>
              <p class="m-0 text-color-secondary text-sm">Upload any safety photos, shift paperwork, screenshots, or supporting files for this report. You can add an optional description to each document after selecting it.</p>
            </div>
          </ng-template>
        </p-fileUpload>
      </div>
    }

    @if (savedAttachments().length) {
      <div class="attachment-list">
        @for (att of savedAttachments(); track att.id ?? att.fileName ?? $index) {
          <div class="attachment-card attachment-card-saved">
            <div class="attachment-card-preview">
              @if (isSavedAttachmentImage(att) && getSavedAttachmentPreviewUrl(att)) {
                <img
                  [src]="getSavedAttachmentPreviewUrl(att)!"
                  [alt]="att.fileName"
                  class="attachment-card-image">
              } @else {
                <div class="attachment-card-icon">
                  <i class="pi pi-file"></i>
                </div>
              }
            </div>

            <div class="attachment-card-body">
              <div class="attachment-card-title">
                <a class="cursor-pointer text-primary" (click)="downloadAttachment(att.id)">{{ att.fileName }}</a>
              </div>
              @if (att.sizeBytes) {
                <div class="attachment-card-meta">
                  {{ (att.sizeBytes / 1024) | number:'1.0-0' }} KB
                </div>
              }

              <label [for]="'saved-description-' + att.id" class="attachment-card-label">Document Description</label>
              <textarea
                [id]="'saved-description-' + att.id"
                pTextarea
                rows="2"
                [(ngModel)]="att.description"
                [disabled]="isReadOnly()"
                class="w-full"
                placeholder="Optional description for this file">
              </textarea>
            </div>

            <div class="attachment-card-actions">
              <p-button
                icon="pi pi-download"
                class="p-button-text p-button-sm"
                (onClick)="downloadAttachment(att.id)">
              </p-button>
              @if (!isReadOnly()) {
                <p-button
                  icon="pi pi-save"
                  class="p-button-text p-button-sm"
                  [loading]="isSavingAttachmentDescription(att.id)"
                  [disabled]="!hasSavedAttachmentDescriptionChanged(att)"
                  (onClick)="saveAttachmentDescription(att)">
                </p-button>
                <p-button
                  icon="pi pi-trash"
                  class="p-button-text p-button-sm p-button-danger"
                  (onClick)="removeAttachment(att.id)">
                </p-button>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <span class="text-color-secondary">No files attached.</span>
    }
  </p-panel>

  @if (currentSummary(); as s) {
    @if (s.outputSnapshot?.outputTitle) {
      <div class="output-title mb-4">
        <h3>{{ s.outputSnapshot!.outputTitle }}</h3>
      </div>
    }

    <p-panel header="Job Identification" class="mb-3">
      <div class="grid">
        <div class="col-12 md:col-4">
          <span class="field-label">Customer</span>
          <span class="field-value">{{ formatNA(s.outputSnapshot?.customer) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">ESS Job Number</span>
          <span class="field-value">{{ formatNA(s.outputSnapshot?.essJobNumber) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">Well Name</span>
          <span class="field-value">{{ formatNA(s.outputSnapshot?.wellName) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">District</span>
          <span class="field-value">{{ formatNA(s.outputSnapshot?.district) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">Date &amp; Time of Report</span>
          <span class="field-value">{{ s.outputSnapshot?.reportDateTimeUtc | date:'medium' }}</span>
        </div>
      </div>
    </p-panel>

    <p-panel header="Shift Details" class="mb-3">
      <div class="grid">
        <div class="col-12 md:col-3">
          <span class="field-label">Shift Start</span>
          <span class="field-value">{{ s.shiftStartUtc | date:'medium' }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Shift End</span>
          <span class="field-value">{{ s.shiftEndUtc | date:'medium' }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Day / Night</span>
          <span class="field-value">{{ formatNA(s.dayNight) }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Total Hours Worked</span>
          <span class="field-value">{{ formatNumber(s.totalHoursWorked) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">Crew Members</span>
          <span class="field-value">{{ formatNA(s.crewMembers) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">Current Status</span>
          <span class="field-value">{{ formatNA(s.currentStatus) }}</span>
        </div>
      </div>
    </p-panel>

    <p-panel header="Safety" class="mb-3">
      <div class="grid">
        <div class="col-12">
          <span class="field-label">Safety Topic(s) Discussed</span>
          <span class="field-value">{{ formatNA(s.safetyTopicsDiscussed) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">Any Safety Incidents?</span>
          <span class="field-value">{{ formatYesNo(s.anySafetyIncidents) }}</span>
        </div>
        @if (s.anySafetyIncidents === true) {
          <div class="col-12 md:col-8">
            <span class="field-label">Safety Incident Details</span>
            <span class="field-value">{{ formatNA(s.safetyIncidentDetails) }}</span>
          </div>
          <div class="col-12">
            <span class="field-label">Incident Types</span>
            <span class="field-value">{{ s.safetyIncidents?.length ? s.safetyIncidents!.join(', ') : 'N/A' }}</span>
          </div>
        }
      </div>
    </p-panel>

    <p-panel header="Operational Checks" class="mb-3">
      <div class="grid">
        <div class="col-12 md:col-4">
          <span class="field-label">Data Components Working?</span>
          <span class="field-value">{{ formatYesNo(s.dataComponentsWorking) }}</span>
        </div>
        @if (s.dataComponentsWorking === false) {
          <div class="col-12 md:col-8">
            <span class="field-label">Data Components Context</span>
            <span class="field-value">{{ formatNA(s.dataComponentsContext) }}</span>
          </div>
        }
        <div class="col-12 md:col-4">
          <span class="field-label">{{ fluidTypeOutputLabel() }}</span>
          <span class="field-value">{{ formatNA(s.fluidType) }}</span>
        </div>
        @if (supportsCurrentFluidSystem()) {
          <div class="col-12 md:col-4">
            <span class="field-label">Current Fluid System</span>
            <span class="field-value">{{ formatNA(s.currentFluidSystem) }}</span>
          </div>
        }
        @if (supportsFoamMetrics()) {
          <div class="col-12 md:col-4">
            <span class="field-label">Foam Stability Time (min)</span>
            <span class="field-value">{{ formatNA(s.foamStabilityMinutes) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Sand Suspension Time (min)</span>
            <span class="field-value">{{ formatNA(s.sandSuspensionMinutes) }}</span>
          </div>
        }
        <div class="col-12 md:col-4">
          <span class="field-label">Fuel Provided by Customer?</span>
          <span class="field-value">{{ formatYesNo(s.fuelProvidedByCustomer) }}</span>
        </div>
      </div>
    </p-panel>

    <p-panel header="Narratives" class="mb-3">
      <div class="grid">
        <div class="col-12">
          <span class="field-label">Job Status</span>
          <p class="field-value pre-wrap">{{ formatNA(s.jobStatus) }}</p>
        </div>
        <div class="col-12">
          <span class="field-label">Last Discussion with Customer</span>
          <p class="field-value pre-wrap">{{ formatNA(s.customerFeedback) }}</p>
        </div>
        <div class="col-12">
          <span class="field-label">Mechanical Needs</span>
          <p class="field-value pre-wrap">{{ formatNA(s.mechanicalNeeds) }}</p>
        </div>
        <div class="col-12">
          <span class="field-label">Next Planned Work</span>
          <p class="field-value pre-wrap">{{ formatNA(s.nextPlannedWork) }}</p>
        </div>
      </div>
    </p-panel>

    <p-panel header="Attachments" class="mb-3">
      @if (s.attachments?.length) {
        <div class="attachment-output-list">
          @for (att of s.attachments!; track att.id ?? att.fileName ?? $index) {
            <div class="attachment-output-item">
              @if (isSavedAttachmentImage(att) && getSavedAttachmentPreviewUrl(att)) {
                <div class="attachment-output-preview">
                  <img
                    [src]="getSavedAttachmentPreviewUrl(att)!"
                    [alt]="att.fileName"
                    class="attachment-output-image cursor-pointer"
                    (click)="downloadAttachment(att.id)">
                </div>
              }
              <span class="field-label">
                <a class="cursor-pointer text-primary" (click)="downloadAttachment(att.id)">{{ formatNA(att.fileName) }}</a>
                @if (att.sizeBytes) {
                  <span class="ml-2 text-color-secondary text-sm">
                    ({{ (att.sizeBytes / 1024) | number:'1.0-0' }} KB)
                  </span>
                }
              </span>
              <span class="field-value">{{ formatNA(att.description) }}</span>
            </div>
          }
        </div>
      } @else {
        <span class="field-value">No files attached.</span>
      }
    </p-panel>

    @if (s.outputSnapshot) {
      <p-panel header="Job Progression" class="mb-3">
        <div class="grid">
          <div class="col-12 md:col-4">
            <span class="field-label">PBTD</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.pbtd) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Current Depth</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.currentDepth) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Deepest Depth Recorded</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.deepestDepthRecorded) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Footage Moved During Shift</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.footageMovedDuringShift) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">ROP Over Shift</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.ropOverShift) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Footage Remaining</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.footageRemaining) }}</span>
          </div>
        </div>
      </p-panel>

      <p-panel header="Live Data Status" class="mb-3">
        <div class="grid">
          <div class="col-12 md:col-4">
            <span class="field-label">Live Data Pushed Over Shift</span>
            <span class="field-value">{{ formatPercentage(s.outputSnapshot.liveDataPushedPercent) }}</span>
          </div>
        </div>

        @if (s.outputSnapshot.devicePushStatuses?.length) {
          <p-table
            [value]="s.outputSnapshot.devicePushStatuses!"
            [tableStyle]="{'min-width': '30rem'}">
            <ng-template pTemplate="header">
              <tr>
                <th>Device</th>
                <th>Last Data Pushed</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ formatNA(row.deviceName) }}</td>
                <td>{{ row.lastDataPushedAtUtc ? (row.lastDataPushedAtUtc | date:'medium') : 'N/A' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </p-panel>
    }

    @if (s.outputSnapshot?.liveDataReport?.length) {
      <p-panel header="Live Data Report" class="mb-3">
        <p-table [value]="s.outputSnapshot!.liveDataReport!" [tableStyle]="{'min-width': '30rem'}">
          <ng-template pTemplate="header">
            <tr>
              <th>Metric</th>
              <th>Avg Over Shift</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.metricLabel }}</td>
              <td>{{ formatNumber(row.avgOverShift) }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-panel>
    }

    @if (s.outputSnapshot?.workbookDataReport?.length) {
      <p-panel header="Workbook Data Report" class="mb-3">
        <p-table [value]="s.outputSnapshot!.workbookDataReport!" [tableStyle]="{'min-width': '40rem'}">
          <ng-template pTemplate="header">
            <tr>
              <th>Metric</th>
              <th># of Entries</th>
              <th>Current</th>
              <th>Avg Over Shift</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.metricLabel }}</td>
              <td>{{ formatNA(row.numberOfEntries) }}</td>
              <td>{{ formatNumber(row.current) }}</td>
              <td>{{ formatNumber(row.avgOverShift) }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-panel>
    }

    @if (s.outputSnapshot) {
      <p-panel header="LCM Report" class="mb-3">
        <div class="grid">
          <div class="col-12 md:col-4">
            <span class="field-label">Part C Test Status</span>
            <span class="field-value">{{ formatPartCTestStatus(s.partCTestStatus) }}</span>
          </div>
          @if (showPartCDetailsForValue(s.partCTestStatus)) {
            <div class="col-12 md:col-4">
              <span class="field-label">Grams to Pass</span>
              <span class="field-value">{{ formatNA(s.gramsToPass) }}</span>
            </div>
            <div class="col-12 md:col-4">
              <span class="field-label">Water Leak Off</span>
              <span class="field-value">{{ formatNA(s.waterLeakOff) }}</span>
            </div>
          }
          <div class="col-12 md:col-4">
            <span class="field-label">BBLS of LCM Pumped</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.bblsOfLcmPumped) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">LBS of Part C</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.lbsOfPartC) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Avg LBS Part C / BBL</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.avgLbsPartCPerBbl) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">LBS of OBP</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.lbsOfObp) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Avg LBS OBP / BBL</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.avgLbsObpPerBbl) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">LBS of Fiber</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.lbsOfFiber) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Avg LBS Fiber / BBL</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.avgLbsFiberPerBbl) }}</span>
          </div>
        </div>
      </p-panel>
    }

    @if (shiftSummaryId) {
      <app-chemical-dosage-chart
        [shiftSummaryId]="shiftSummaryId"
        [parquetBlobPath]="s.outputSnapshot?.chemicalDosageParquetBlobPath">
      </app-chemical-dosage-chart>
    }

    @if (s.outputSnapshot?.dashboardUrl) {
      <div class="flex justify-content-between mt-4">
        <p-button
          label="Open Dashboard for this Shift"
          icon="pi pi-external-link"
          class="p-button-outlined"
          (onClick)="openDashboard(s.outputSnapshot!.dashboardUrl)">
        </p-button>
      </div>
    }
  }

  <div class="flex justify-content-end gap-2 mt-4">
    <p-button label="Back" class="p-button-outlined" (onClick)="cancel()"></p-button>
    @if (!isReadOnly()) {
      <p-button
        label="Save Draft"
        icon="pi pi-save"
        [loading]="saving()"
        (onClick)="saveDraft()">
      </p-button>
      <p-button
        label="Submit"
        icon="pi pi-check"
        [loading]="submitting()"
        (onClick)="submit()">
      </p-button>
    }
  </div>
</div>
